import { useState } from 'react';
import { Link } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string;
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'USFansSpreadsheet: Co to jest i jak działa?',
      answer: 'USFansSpreadsheet to kompleksowe narzędzie do zarządzania arkuszami kalkulacyjnymi, które łączy elastyczność tradycyjnych arkuszy z nowoczesnymi funkcjami automatyzacji. Umożliwia tworzenie, edycję i udostępnianie arkuszy online, oferując przy tym zaawansowane możliwości analizy danych, integracje z zewnętrznymi narzędziami oraz współpracę zespołową w czasie rzeczywistym. Platforma działa w przeglądarce, co oznacza, że nie wymaga instalacji dodatkowego oprogramowania.',
    },
    {
      question: 'Czy USFansSpreadsheet jest bezpieczny?',
      answer: 'Tak, USFansSpreadsheet stosuje wielowarstwowe zabezpieczenia, w tym szyfrowanie AES-256 dla danych w spoczynku i podczas transmisji, uwierzytelnianie dwuskładnikowe, regularne audyty bezpieczeństwa oraz automatyczne kopie zapasowe. Platforma jest zgodna z międzynarodowymi standardami ochrony danych, co czyni ją bezpiecznym wyborem zarówno dla użytkowników indywidualnych, jak i firm.',
    },
    {
      question: 'Jak korzystać z USFansSpreadsheet krok po kroku?',
      answer: 'Rozpoczęcie pracy z USFansSpreadsheet jest proste. Po utworzeniu konta możesz od razu tworzyć nowe arkusze lub importować istniejące pliki. Interfejs jest intuicyjny – pasek narzędzi przypomina znane aplikacje biurowe. Dla początkujących dostępne są gotowe szablony, które przyspieszają rozpoczęcie pracy. Zachęcamy do zapoznania się z naszym kompletnym przewodnikiem, gdzie szczegółowo opisujemy każdy krok.',
    },
    {
      question: 'USFansSpreadsheet vs Excel – które rozwiązanie wybrać?',
      answer: 'Wybór między USFansSpreadsheet a Excel zależy od Twoich potrzeb. USFansSpreadsheet oferuje lepszą współpracę online, wbudowaną automatyzację i nowoczesny interfejs dostępny z każdego urządzenia. Excel sprawdza się w zaawansowanych analizach offline i ma dłuższą historię na rynku. Dla zespołów pracujących zdalnie i potrzebujących elastycznego narzędzia online, USFansSpreadsheet jest często lepszym wyborem.',
    },
    {
      question: 'Jakie są najlepsze alternatywy dla USFansSpreadsheet?',
      answer: 'Na rynku dostępnych jest kilka alternatyw dla USFansSpreadsheet, w tym Google Sheets, Microsoft Excel Online, Airtable, Smartsheet i Zoho Sheet. Każde z tych narzędzi ma swoje mocne strony. Google Sheets oferuje doskonałą integrację z ekosystemem Google, Airtable łączy arkusze z bazami danych, a Smartsheet jest idealny do zarządzania projektami. Wybór zależy od konkretnych potrzeb.',
    },
    {
      question: 'Czy USFansSpreadsheet oferuje darmowy okres próbny?',
      answer: 'Tak, USFansSpreadsheet oferuje bezpłatny okres próbny, który pozwala przetestować wszystkie kluczowe funkcje platformy przed podjęciem decyzji o subskrypcji. W okresie próbnym masz dostęp do pełnej funkcjonalności, w tym zaawansowanych narzędzi analitycznych, szablonów i funkcji współpracy zespołowej.',
    },
    {
      question: 'Czy mogę importować dane z Excela do USFansSpreadsheet?',
      answer: 'Oczywiście! USFansSpreadsheet obsługuje import plików w formatach .xlsx, .xls, .csv i wielu innych. Proces importu jest prosty – wystarczy przeciągnąć plik do interfejsu lub użyć opcji importu z menu. Platforma zachowuje formatowanie, formuły i strukturę danych, co minimalizuje czas potrzebny na migrację.',
    },
    {
      question: 'Jak często USFansSpreadsheet jest aktualizowany?',
      answer: 'USFansSpreadsheet jest regularnie aktualizowany – nowe funkcje i poprawki bezpieczeństwa są wdrażane co 2-4 tygodnie. Zespół deweloperski aktywnie słucha opinii społeczności i na ich podstawie priorytetyzuje rozwój nowych funkcjonalności. Wszystkie aktualizacje są automatyczne i nie wymagają żadnych działań ze strony użytkownika.',
    },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-[800px] mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
            FAQ
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
            Najczęściej zadawane pytania o USFansSpreadsheet
          </h2>
          <p className="text-foreground-600 max-w-[600px] mx-auto leading-relaxed">
            Znajdź odpowiedzi na najczęstsze pytania dotyczące USFansSpreadsheet. 
            Jeśli nie znalazłeś odpowiedzi, sprawdź nasz pełny przewodnik.
          </p>
        </div>

        <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="rounded-xl border border-background-200/70 overflow-hidden"
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-background-100/50 transition-colors cursor-pointer"
                aria-expanded={openIndex === index}
              >
                <h3 className="font-semibold text-foreground-950 text-sm md:text-base" itemProp="name">
                  {faq.question}
                </h3>
                <span className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full transition-transform duration-200 ${
                  openIndex === index ? 'bg-primary-100 text-primary-600 rotate-45' : 'bg-background-100 text-foreground-500'
                }`}>
                  <i className="ri-add-line text-xs"></i>
                </span>
              </button>
              <div
                className={`transition-all duration-300 ${
                  openIndex === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
              >
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed" itemProp="text">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/artykul/kompletny-przewodnik-usfansspreadsheet-2026"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
          >
            Zobacz więcej w pełnym przewodniku
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}